import { cn } from '@/utils/cn'
import { type ComponentProps, forwardRef } from 'react'
import { ArrowIcon } from '../icons/arrow'
import { DotsIcon } from '../icons/dots'
import { Button, type ButtonProps } from './button'

export const Pagination = ({ className, ...props }: ComponentProps<'nav'>) => (
  <nav aria-label="pagination" className={cn('flex items-center', className)} {...props} />
)
Pagination.displayName = 'Pagination'

export const PaginationContent = forwardRef<HTMLUListElement, ComponentProps<'ul'>>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn('flex flex-row items-center gap-2', className)} {...props} />
))
PaginationContent.displayName = 'PaginationContent'

export const PaginationItem = forwardRef<HTMLLIElement, ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
))
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
  isActive?: boolean
}

export const PaginationButton = ({
  className,
  variant = 'default',
  size = 'sm',
  isActive,
  ...props
}: PaginationLinkProps & ButtonProps) => (
  <Button
    variant={isActive ? 'input' : variant}
    size={size}
    className={cn('w-8 gap-2 rounded-xl px-2', className)}
    aria-current={isActive ? 'page' : undefined}
    {...props}
  />
)
PaginationButton.displayName = 'PaginationButton'

export const PaginationPrevious = ({ className, size = 'sm', ...props }: ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label="Go to previous page"
    size={size}
    variant="input"
    className={cn('w-auto px-5', 'disabled:bg-page disabled:hocus:bg-page', className)}
    {...props}
  >
    <ArrowIcon size={16} direction="left" />
    <span>Previous</span>
  </PaginationButton>
)
PaginationPrevious.displayName = 'PaginationPrevious'

export const PaginationNext = ({ className, size = 'sm', ...props }: ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label="Go to next page"
    size={size}
    variant="input"
    className={cn('w-auto px-5', 'disabled:bg-page disabled:hocus:bg-page', className)}
    {...props}
  >
    <span>Next</span>
    <ArrowIcon size={16} direction="right" />
  </PaginationButton>
)
PaginationNext.displayName = 'PaginationNext'

export const PaginationEllipsis = ({ className, ...props }: ComponentProps<'span'>) => (
  <span aria-hidden className={cn('flex h-8 w-8 items-center justify-center', className)} {...props}>
    <DotsIcon size={16} />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'
